import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, FileEdit, LogOut, Trash2, RefreshCw, Save, RotateCcw, Loader2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SECTIONS } from "./editorSchema";
import { SchemaForm } from "./SchemaForm";
import { DEFAULT_CONTENT } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

// Retry once after refreshing the access token
const call = async (fn) => {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status === 401) {
      await api.post("/auth/refresh");
      return await fn();
    }
    throw e;
  }
};

const EnquiriesTab = () => {
  const [items, setItems] = useState(null);
  const load = () =>
    call(() => api.get("/admin/enquiries"))
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load enquiries"));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    try {
      await call(() => api.delete(`/admin/enquiries/${id}`));
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (items === null)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
      </div>
    );

  return (
    <div data-testid="enquiries-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          <strong className="text-brand-navy">{items.length}</strong> enquiry(ies) received
        </p>
        <button onClick={load} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-brand-blue hover:bg-brand-sky" data-testid="enquiries-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center text-sm text-slate-400" data-testid="enquiries-empty">
          No enquiries yet. New quote requests from the website will appear here.
        </div>
      )}
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid={`enquiry-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-base font-bold text-brand-navy">
                  {e.name} <span className="ml-2 text-sm font-semibold text-brand-blue">{e.mobile}</span>
                </p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {e.location} · {e.property_type} · {e.solar_requirement}
                  {e.monthly_units ? ` · ${e.monthly_units} units/mo` : ""}
                </p>
                {e.email && <p className="mt-0.5 text-sm text-slate-500">{e.email}</p>}
                {e.message && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{e.message}</p>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="text-xs text-slate-400">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : ""}</span>
                <button onClick={() => remove(e.id)} className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50" data-testid={`enquiry-delete-${e.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EditorTab = () => {
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
  const [value, setValue] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const section = useMemo(() => SECTIONS.find((s) => s.key === activeKey), [activeKey]);

  const load = (key) => {
    setValue(null);
    call(() => api.get(`/content/${key}`))
      .then((r) => {
        setValue(JSON.parse(JSON.stringify(r.data.value)));
        setIsCustom(true);
      })
      .catch(() => {
        setValue(JSON.parse(JSON.stringify(DEFAULT_CONTENT[key])));
        setIsCustom(false);
      });
  };

  useEffect(() => {
    load(activeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const save = async () => {
    setSaving(true);
    try {
      await call(() => api.put(`/admin/content/${activeKey}`, { value }));
      setIsCustom(true);
      toast.success(`"${section.label}" saved. The live website now uses this content.`);
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await call(() => api.delete(`/admin/content/${activeKey}`));
      setValue(JSON.parse(JSON.stringify(DEFAULT_CONTENT[activeKey])));
      setIsCustom(false);
      toast.success(`"${section.label}" reset to the original content.`);
    } catch {
      toast.error("Reset failed.");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-4" data-testid="editor-tab">
      <div className="lg:col-span-1">
        <div className="rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              className={`w-full rounded-xl px-4 py-2.5 text-left font-heading text-sm font-semibold transition-colors ${
                activeKey === s.key ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-brand-sky"
              }`}
              data-testid={`editor-section-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-bold text-brand-navy">{section.label}</h2>
              <p className="text-xs text-slate-400">
                {isCustom ? "Showing your customised content" : "Showing default content — save to publish your changes"}
              </p>
            </div>
            <div className="flex gap-2">
              {isCustom && (
                <button onClick={reset} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50" data-testid="editor-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to Default
                </button>
              )}
              <button
                onClick={save}
                disabled={saving || value === null}
                className="flex items-center gap-1.5 rounded-full bg-brand-orange px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-orangedark disabled:opacity-60"
                data-testid="editor-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save & Publish
              </button>
            </div>
          </div>
          {value === null ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
            </div>
          ) : (
            <SchemaForm fields={section.fields} value={value} onChange={setValue} sectionKey={activeKey} />
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    call(() => api.get("/auth/me"))
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/admin/login");
  };

  if (!user)
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-sky">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );

  return (
    <div className="min-h-screen bg-brand-sky" data-testid="admin-dashboard">
      <Seo title="Admin Panel | DHIYAA SOLAR ENERGY" description="Manage website content and enquiries" />
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-horizontal.webp" alt="DHIYAA SOLAR ENERGY" className="h-10 w-auto" />
            <span className="rounded-full bg-brand-navy px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-widest text-brand-orange">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-brand-blue hover:bg-brand-sky" data-testid="admin-view-site">
              View Website
            </Link>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-full bg-brand-navy px-4 py-2 text-xs font-bold text-white hover:bg-brand-bluedark" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setTab("enquiries")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-heading text-sm font-bold transition-colors ${
              tab === "enquiries" ? "bg-brand-orange text-white" : "bg-white text-slate-600 hover:bg-white/70"
            }`}
            data-testid="tab-enquiries"
          >
            <Inbox className="h-4 w-4" /> Enquiries
          </button>
          <button
            onClick={() => setTab("editor")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-heading text-sm font-bold transition-colors ${
              tab === "editor" ? "bg-brand-orange text-white" : "bg-white text-slate-600 hover:bg-white/70"
            }`}
            data-testid="tab-editor"
          >
            <FileEdit className="h-4 w-4" /> Page Editor
          </button>
        </div>
        {tab === "enquiries" ? <EnquiriesTab /> : <EditorTab />}
      </div>
    </div>
  );
}
